import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';

import { cn } from '@/design-system/lib/cn';

import {
  MONTHS,
  WEEKDAYS,
  addDaysIso,
  addMonthsIso,
  clampIso,
  longLabel,
  monthGrid,
  parseIso,
  toIso,
  todayIso,
} from './dates';

type View = 'days' | 'months' | 'years';

export type CalendarProps = {
  /** Dia marcado (ISO). */
  value?: string | null;
  /** Intervalo destacado: as pontas marcadas e os dias do meio tingidos. */
  range?: { from: string | null; to: string | null };
  min?: string;
  max?: string;
  onSelect: (iso: string) => void;
  /** Mês que abre sem valor (ISO de qualquer dia dele); sem ele, o de hoje. */
  defaultMonth?: string;
  /** Leva o foco ao dia marcado ao montar (quando abre no popover). */
  focusOnMount?: boolean;
  className?: string;
};

const DAY_STEP: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
const YEARS_PER_PAGE = 12;

/**
 * Calendário do mês em grade (`role="grid"`): setas andam por dia e semana, PageUp/PageDown
 * por mês (com Shift, por ano), Home/End vão ao começo e ao fim da semana. O título abre a
 * escolha do mês e, dela, a do ano — para datas distantes, como a de nascimento.
 */
export function Calendar({
  value,
  range,
  min,
  max,
  onSelect,
  defaultMonth,
  focusOnMount = false,
  className,
}: CalendarProps) {
  const today = todayIso();
  const selected = parseIso(value) ? value! : null;
  const [cursor, setCursor] = useState(() =>
    clampIso(selected ?? range?.from ?? (parseIso(defaultMonth) ? defaultMonth! : today), min, max)
  );
  const [view, setView] = useState<View>('days');
  const focusDay = useRef(focusOnMount);
  const gridRef = useRef<HTMLTableElement>(null);
  const { y, m } = parseIso(cursor)!;

  // Depois de andar pelo teclado (ou ao abrir), o foco acompanha o dia do cursor.
  useEffect(() => {
    if (!focusDay.current || view !== 'days') return;
    focusDay.current = false;
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-iso="${cursor}"]`)?.focus();
  }, [cursor, view]);

  const outOfRange = (iso: string) => (!!min && iso < min) || (!!max && iso > max);
  const move = (next: string) => {
    focusDay.current = true;
    setCursor(clampIso(next, min, max));
  };

  const onDayKey = (e: KeyboardEvent, iso: string) => {
    const weekday = new Date(`${iso}T12:00:00`).getDay();
    const step = DAY_STEP[e.key];
    let next: string | null = null;

    if (step) next = addDaysIso(iso, step);
    else if (e.key === 'PageUp') next = addMonthsIso(iso, e.shiftKey ? -12 : -1);
    else if (e.key === 'PageDown') next = addMonthsIso(iso, e.shiftKey ? 12 : 1);
    else if (e.key === 'Home') next = addDaysIso(iso, -weekday);
    else if (e.key === 'End') next = addDaysIso(iso, 6 - weekday);

    if (!next) return;

    e.preventDefault();
    move(next);
  };

  const yearStart = y - (y % YEARS_PER_PAGE);
  const minYear = parseIso(min)?.y ?? -Infinity;
  const maxYear = parseIso(max)?.y ?? Infinity;
  const monthOff = (year: number, month: number) =>
    (!!min && toIso({ y: year, m: month + 1, d: 0 }) < min) ||
    (!!max && toIso({ y: year, m: month, d: 1 }) > max);

  const step = (dir: -1 | 1) => {
    if (view === 'days') setCursor(clampIso(addMonthsIso(cursor, dir), min, max));
    else if (view === 'months') setCursor(clampIso(addMonthsIso(cursor, dir * 12), min, max));
    else setCursor(clampIso(addMonthsIso(cursor, dir * 12 * YEARS_PER_PAGE), min, max));
  };
  const prevDisabled =
    view === 'years'
      ? yearStart <= minYear
      : !!min && toIso({ y: view === 'days' ? y : y - 1, m: view === 'days' ? m : 11, d: 0 }) < min;
  const nextDisabled =
    view === 'years'
      ? yearStart + YEARS_PER_PAGE > maxYear
      : !!max && toIso(view === 'days' ? { y, m: m + 1, d: 1 } : { y: y + 1, m: 0, d: 1 }) > max;

  const captions: Record<View, string> = {
    days: `${MONTHS[m]!.charAt(0).toUpperCase()}${MONTHS[m]!.slice(1)} de ${y}`,
    months: String(y),
    years: `${yearStart} – ${yearStart + YEARS_PER_PAGE - 1}`,
  };
  const stepLabels: Record<View, [string, string]> = {
    days: ['Mês anterior', 'Próximo mês'],
    months: ['Ano anterior', 'Próximo ano'],
    years: ['Anos anteriores', 'Próximos anos'],
  };
  const pick = 'grid place-items-center rounded-control-sm text-small font-medium transition-colors';

  return (
    <div className={cn('flex w-72 max-w-full flex-col gap-2 p-1', className)}>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label={stepLabels[view][0]}
          disabled={prevDisabled}
          onClick={() => step(-1)}
          className="grid size-8 place-items-center rounded-control-sm text-ink-secondary hover:bg-surface-muted disabled:opacity-35"
        >
          <ChevronLeft aria-hidden className="size-4 stroke-2" />
        </button>
        <button
          type="button"
          disabled={view === 'years'}
          aria-label={view === 'days' ? `${captions.days}, escolher mês` : `${captions.months}, escolher ano`}
          onClick={() => setView(view === 'days' ? 'months' : 'years')}
          className="h-8 flex-1 rounded-control-sm text-control font-bold text-ink hover:bg-surface-muted disabled:hover:bg-transparent"
        >
          {captions[view]}
        </button>
        <button
          type="button"
          aria-label={stepLabels[view][1]}
          disabled={nextDisabled}
          onClick={() => step(1)}
          className="grid size-8 place-items-center rounded-control-sm text-ink-secondary hover:bg-surface-muted disabled:opacity-35"
        >
          <ChevronRight aria-hidden className="size-4 stroke-2" />
        </button>
      </div>

      {view === 'days' ? (
        <table ref={gridRef} role="grid" aria-label={captions.days} className="w-full border-collapse">
          <thead>
            <tr>
              {WEEKDAYS.map((w) => (
                <th
                  key={w.long}
                  scope="col"
                  abbr={w.long}
                  className="h-7 text-micro font-bold text-ink-subtle"
                >
                  {w.short}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }, (_, week) => (
              <tr key={week}>
                {monthGrid(y, m)
                  .slice(week * 7, week * 7 + 7)
                  .map((iso) => {
                    const inMonth = parseIso(iso)!.m === m;
                    const isSelected = iso === selected || iso === range?.from || iso === range?.to;
                    const inBetween = !!range?.from && !!range.to && iso > range.from && iso < range.to;
                    const off = outOfRange(iso);

                    return (
                      <td
                        key={iso}
                        role="gridcell"
                        aria-selected={isSelected || inBetween}
                        className={cn(
                          'p-0 text-center',
                          inBetween && 'bg-primary-soft',
                          iso === range?.from &&
                            range.to &&
                            range.to > iso &&
                            'rounded-l-control-sm bg-primary-soft',
                          iso === range?.to &&
                            range.from &&
                            range.from < iso &&
                            'rounded-r-control-sm bg-primary-soft'
                        )}
                      >
                        <button
                          type="button"
                          data-iso={iso}
                          tabIndex={iso === cursor ? 0 : -1}
                          disabled={off}
                          aria-label={longLabel(iso)}
                          aria-current={iso === today ? 'date' : undefined}
                          onClick={() => onSelect(iso)}
                          onKeyDown={(e) => onDayKey(e, iso)}
                          onFocus={() => iso !== cursor && setCursor(iso)}
                          className={cn(
                            'mx-auto grid size-9 place-items-center rounded-control-sm text-small transition-colors disabled:cursor-not-allowed disabled:opacity-30',
                            isSelected
                              ? 'bg-primary font-bold text-white'
                              : 'font-medium hover:bg-surface-muted',
                            !isSelected && !inMonth && 'text-ink-placeholder',
                            !isSelected && inMonth && 'text-ink',
                            !isSelected &&
                              iso === today &&
                              'font-bold text-primary ring-1 ring-primary-line ring-inset'
                          )}
                        >
                          <span data-numeric aria-hidden>
                            {parseIso(iso)!.d}
                          </span>
                        </button>
                      </td>
                    );
                  })}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {view === 'months' ? (
        <div role="group" aria-label={`Meses de ${y}`} className="grid grid-cols-3 gap-1.5">
          {MONTHS.map((name, i) => {
            const current = parseIso(selected)?.y === y && parseIso(selected)?.m === i;

            return (
              <button
                key={name}
                type="button"
                aria-pressed={current}
                disabled={monthOff(y, i)}
                onClick={() => {
                  focusDay.current = true;
                  setCursor(clampIso(toIso({ y, m: i, d: 1 }), min, max));
                  setView('days');
                }}
                className={cn(
                  pick,
                  'h-11 capitalize disabled:opacity-30',
                  current ? 'bg-primary text-white' : 'text-ink hover:bg-surface-muted'
                )}
              >
                {name.slice(0, 3)}
              </button>
            );
          })}
        </div>
      ) : null}

      {view === 'years' ? (
        <div role="group" aria-label="Anos" className="grid grid-cols-3 gap-1.5">
          {Array.from({ length: YEARS_PER_PAGE }, (_, i) => yearStart + i).map((year) => {
            const current = parseIso(selected)?.y === year;

            return (
              <button
                key={year}
                type="button"
                aria-pressed={current}
                disabled={year < minYear || year > maxYear}
                onClick={() => {
                  setCursor(clampIso(toIso({ y: year, m, d: 1 }), min, max));
                  setView('months');
                }}
                className={cn(
                  pick,
                  'h-11 disabled:opacity-30',
                  current ? 'bg-primary text-white' : 'text-ink hover:bg-surface-muted',
                  !current && year === new Date().getFullYear() && 'font-bold text-primary'
                )}
              >
                <span data-numeric>{year}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
